import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDownIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { computeFloatingDropdownLayout } from '../../utils/floatingDropdownLayout';

const DEFAULT_LIMIT = 25;

const normalizePagination = (pagination, fallbackLimit) => ({
  page: Number(pagination?.page || 1),
  pages: Math.max(1, Number(pagination?.pages || 1)),
  total: Number(pagination?.total || 0),
  limit: Number(pagination?.limit || fallbackLimit || DEFAULT_LIMIT),
});

const mergeUniqueOptions = (currentOptions, nextOptions) => {
  const seen = new Set(currentOptions.map((option) => String(option.value)));
  const merged = [...currentOptions];

  nextOptions.forEach((option) => {
    const key = String(option.value);
    if (!seen.has(key)) {
      seen.add(key);
      merged.push(option);
    }
  });

  return merged;
};

const AsyncSearchableSelect = ({
  label,
  value,
  onChange,
  loadOptions,
  displayLabel = '',
  placeholder = 'Select an option',
  searchPlaceholder = 'Search...',
  className = '',
  error = '',
  required = false,
  disabled = false,
  size = 'sm',
  menuPlacement = 'auto',
  limit = DEFAULT_LIMIT,
  noResultsText = 'No results found',
  emptyText = 'No options available',
  allowClear = true,
  multi = false,
  variant = 'button', // button | combobox | minimal (minimal matches CustomSelect filter bars)
  inactiveLabel = '',
  onCreateClick = null,
  createLabel = '+ Add new…',
  menuMinWidth = 260,
  richOptions = false,
  debounceMs = 300,
  refreshOnOpen = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [options, setOptions] = useState([]);
  const [pagination, setPagination] = useState(() => normalizePagination({}, limit));
  const [loading, setLoading] = useState(false);
  const [selectedLabel, setSelectedLabel] = useState(displayLabel);
  const [dropdownStyle, setDropdownStyle] = useState({});
  const [listMaxHeight, setListMaxHeight] = useState(208);

  const containerRef = useRef(null);
  const portalRef = useRef(null);
  const listRef = useRef(null);
  const searchInputRef = useRef(null);
  const searchTimerRef = useRef(null);
  const requestIdRef = useRef(0);
  const currentSearchRef = useRef('');
  const loadingRef = useRef(false);
  const paginationRef = useRef(normalizePagination({}, DEFAULT_LIMIT));
  const isMinimal = variant === 'minimal';

  const sizeClasses = {
    // Match CustomSelect size="sm|md|lg" trigger height (especially variant="input"|"minimal" in filter bars).
    sm: variant === 'combobox' ? 'text-sm min-h-[34px]' : isMinimal ? 'py-1.5 pl-2 pr-2 text-sm' : 'px-3 py-2 text-sm',
    md: variant === 'combobox' ? 'text-sm min-h-[42px]' : isMinimal ? 'py-2.5 pl-3 pr-2 text-sm' : 'px-4 py-2 text-sm',
    lg: variant === 'combobox' ? 'text-base min-h-[48px]' : isMinimal ? 'py-3 pl-4 pr-2 text-base' : 'px-4 py-2.5 text-base',
  };

  const inputPadding = {
    sm: 'px-2 py-1.5',
    md: 'px-3 py-2.5',
    lg: 'px-4 py-3',
  };

  const chevronSizes = {
    sm: 'h-3 w-3',
    md: 'h-4 w-4',
    lg: 'h-5 w-5',
  };

  const triggerInputRef = useRef(null);

  const computeDropdownStyle = useCallback(() => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const createFooterHeight = onCreateClick ? 44 : 0;
    const headerHeight = (pagination.total > 0 ? 88 : 72) + createFooterHeight;
    const layout = computeFloatingDropdownLayout({
      triggerRect: rect,
      preferredWidth: Math.max(rect.width, menuMinWidth),
      headerHeight,
      placement: menuPlacement,
    });
    setDropdownStyle(layout.style);
    setListMaxHeight(layout.listMaxHeight);
  }, [menuPlacement, menuMinWidth, onCreateClick, pagination.total]);

  const resetOptions = useCallback(() => {
    const emptyPagination = normalizePagination({}, limit);
    paginationRef.current = emptyPagination;
    setOptions([]);
    setPagination(emptyPagination);
  }, [limit]);

  const fetchPage = useCallback(
    async ({ search, page, append }) => {
      if (typeof loadOptions !== 'function') return;

      const searchKey = String(search || '');
      const requestId = ++requestIdRef.current;
      loadingRef.current = true;
      setLoading(true);

      try {
        const result = await loadOptions({ search: searchKey, page, limit });
        if (requestId !== requestIdRef.current) return;
        if (searchKey !== String(currentSearchRef.current || '')) return;

        const nextOptions = Array.isArray(result?.options) ? result.options : [];
        const nextPagination = normalizePagination(result?.pagination, limit);
        paginationRef.current = nextPagination;

        setOptions((prev) => (append ? mergeUniqueOptions(prev, nextOptions) : nextOptions));
        setPagination(nextPagination);

      } catch (fetchError) {
        console.error('AsyncSearchableSelect: failed to load options', fetchError);
        if (!append && requestId === requestIdRef.current) {
          setOptions([]);
          const emptyPagination = normalizePagination({}, limit);
          paginationRef.current = emptyPagination;
          setPagination(emptyPagination);
        }
      } finally {
        if (requestId === requestIdRef.current) {
          loadingRef.current = false;
          setLoading(false);
        }
      }
    },
    [limit, loadOptions]
  );

  useEffect(() => {
    if (multi) return; // multi mode: display is computed by parent via displayLabel prop
    if (!value) {
      setSelectedLabel('');
      return;
    }

    if (displayLabel) {
      setSelectedLabel(displayLabel);
      return;
    }

    const matchedOption = options.find((option) => String(option.value) === String(value));
    if (matchedOption?.label) {
      setSelectedLabel(matchedOption.label);
    }
  }, [displayLabel, multi, options, value]);

  useEffect(() => {
    if (!isOpen) {
      setSearchTerm('');
      currentSearchRef.current = '';
      return;
    }

    fetchPage({ search: '', page: 1, append: false });
    // Intentionally not depending on fetchPage: a parent re-render must not
    // wipe an in-progress search with an empty fetch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const handleViewportChange = () => computeDropdownStyle();
    window.addEventListener('scroll', handleViewportChange, true);
    window.addEventListener('resize', handleViewportChange);
    return () => {
      window.removeEventListener('scroll', handleViewportChange, true);
      window.removeEventListener('resize', handleViewportChange);
    };
  }, [computeDropdownStyle, isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const focusInput = () => {
      if (searchInputRef.current) {
        searchInputRef.current.focus();
      }
    };
    focusInput();
    const raf = window.requestAnimationFrame(focusInput);
    const timer = setTimeout(focusInput, 30);
    return () => {
      window.cancelAnimationFrame(raf);
      clearTimeout(timer);
    };
  }, [isOpen]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      const inTrigger = containerRef.current?.contains(event.target);
      const inMenu = portalRef.current?.contains(event.target);
      if (!inTrigger && !inMenu) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const openDropdown = () => {
    if (disabled) return;
    if (isOpen) return;
    computeDropdownStyle();
    setSearchTerm('');
    currentSearchRef.current = '';
    loadingRef.current = true;
    setLoading(true);
    resetOptions();
    setIsOpen(true);
  };

  const handleSearchChange = (event) => {
    const nextSearch = event.target.value;
    setSearchTerm(nextSearch);
    // Mark the latest query immediately so a slower empty/open fetch cannot
    // overwrite the list after the user has already started typing.
    currentSearchRef.current = nextSearch;

    clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(() => {
      resetOptions();
      fetchPage({ search: nextSearch, page: 1, append: false });
    }, debounceMs);
  };

  const handleScroll = () => {
    if (loadingRef.current) return;
    const pag = paginationRef.current;
    if (pag.page >= pag.pages) return;

    const element = listRef.current;
    if (!element) return;

    const isNearBottom = element.scrollTop + element.clientHeight >= element.scrollHeight - 40;
    if (isNearBottom) {
      fetchPage({ search: currentSearchRef.current, page: pag.page + 1, append: true });
    }
  };

  const handleSelect = (option) => {
    if (multi) {
      const current = Array.isArray(value) ? value : [];
      const key = String(option.value);
      const already = current.some((v) => String(v) === key);
      const next = already ? current.filter((v) => String(v) !== key) : [...current, option.value];
      onChange?.(next, option);
    } else {
      setSelectedLabel(option.label || '');
      setIsOpen(false);
      setSearchTerm('');
      onChange?.(option.value, option);
    }
  };

  const handleSelectAll = () => {
    if (!multi || !options.length) return;
    const current = Array.isArray(value) ? value : [];
    const loadedValues = options.map((o) => o.value);
    const allLoadedSelected = loadedValues.every((v) => current.some((c) => String(c) === String(v)));

    let next;
    if (allLoadedSelected) {
      const loadedSet = new Set(loadedValues.map(String));
      next = current.filter((v) => !loadedSet.has(String(v)));
    } else {
      next = [...current];
      for (const v of loadedValues) {
        if (!next.some((c) => String(c) === String(v))) next.push(v);
      }
    }
    onChange?.(next);
  };

  const handleClear = (event) => {
    event.stopPropagation();
    if (multi) {
      onChange?.([]);
    } else {
      setSelectedLabel('');
      setSearchTerm('');
      onChange?.('', null);
    }
  };

  const handleChevronMouseDown = (event) => {
    event.preventDefault();
    event.stopPropagation();
    if (disabled) return;
    if (isOpen) {
      setIsOpen(false);
      setSearchTerm('');
      triggerInputRef.current?.blur();
      return;
    }
    openDropdown();
    triggerInputRef.current?.focus();
  };

  const displayValue = multi
    ? (Array.isArray(value) && value.length > 0 ? `${value.length} selected` : '')
    : (displayLabel || selectedLabel || '');
  const hasSelection = multi ? (Array.isArray(value) && value.length > 0) : Boolean(value);
  const closedInputText = displayValue || inactiveLabel || '';
  const closedInputMuted = !displayValue && !inactiveLabel;

  const allLoadedSelected = multi && options.length > 0
    && options.every((o) => (Array.isArray(value) ? value : []).some((v) => String(v) === String(o.value)));
  const selectAllLabel = (() => {
    if (!multi || !options.length) return '';
    const countLabel = pagination.page < pagination.pages
      ? `${options.length} loaded`
      : String(options.length);
    if (searchTerm.trim()) {
      return allLoadedSelected
        ? `Deselect all matching (${countLabel})`
        : `Select all matching (${countLabel})`;
    }
    return allLoadedSelected
      ? `Deselect all (${countLabel})`
      : `Select all (${countLabel})`;
  })();

  const dropdownMenu = isOpen ? (
    <div ref={portalRef} style={dropdownStyle} className="bg-white border border-gray-200 rounded-lg shadow-lg overflow-hidden">
      <div className="p-2 border-b border-gray-100 bg-white">
        <div className="relative">
          <svg
            className="absolute left-2 top-1/2 -translate-y-1/2 h-3 w-3 text-gray-400 pointer-events-none"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          <input
            ref={searchInputRef}
            type="text"
            value={searchTerm}
            onChange={handleSearchChange}
            placeholder={searchPlaceholder}
            className="w-full pl-6 pr-2 py-1.5 text-xs border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-indigo-500 bg-gray-50"
          />
        </div>
        {pagination.total > 0 && (
          <p className="mt-1 text-right text-xs text-gray-400">
            {pagination.total} item{pagination.total === 1 ? '' : 's'}
          </p>
        )}
      </div>

      <div
        ref={listRef}
        onScroll={handleScroll}
        className="overflow-y-auto"
        style={{ maxHeight: listMaxHeight }}
      >
        {!loading && options.length === 0 && (
          <p className="px-3 py-4 text-center text-xs text-gray-500">
            {currentSearchRef.current ? noResultsText : emptyText}
          </p>
        )}

        {multi && options.length > 0 && (
          <button
            type="button"
            onMouseDown={(e) => {
              e.preventDefault();
              handleSelectAll();
            }}
            className={`w-full px-3 py-2 text-left transition-colors flex items-center gap-2 border-b border-gray-100 font-medium ${
              allLoadedSelected ? 'bg-indigo-50 text-indigo-700' : 'text-gray-900 hover:bg-gray-50'
            }`}
          >
            <span className={`flex-shrink-0 w-3.5 h-3.5 rounded border flex items-center justify-center ${allLoadedSelected ? 'bg-indigo-600 border-indigo-600' : 'border-gray-300'}`}>
              {allLoadedSelected && (
                <svg className="w-2.5 h-2.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                </svg>
              )}
            </span>
            <span className="text-xs">{selectAllLabel}</span>
          </button>
        )}

        {options.map((option) => {
          const isSelected = multi
            ? (Array.isArray(value) ? value : []).some((v) => String(v) === String(option.value))
            : String(option.value) === String(value);
          return (
            <button
              key={option.value}
              type="button"
              onMouseDown={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleSelect(option);
              }}
              className={`w-full px-3 text-left transition-colors flex items-start gap-2 ${richOptions ? 'py-2.5' : 'py-2 items-center'} ${isSelected ? 'bg-indigo-50 text-indigo-700' : 'text-gray-900 hover:bg-gray-50'}`}
            >
              {multi && (
                <span className={`flex-shrink-0 w-3.5 h-3.5 rounded border flex items-center justify-center ${isSelected ? 'bg-indigo-600 border-indigo-600' : 'border-gray-300'}`}>
                  {isSelected && (
                    <svg className="w-2.5 h-2.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                    </svg>
                  )}
                </span>
              )}
              <span className="flex-1 min-w-0">
                <span className={`block leading-tight ${richOptions ? 'text-sm font-semibold font-mono' : 'text-xs font-medium'}`}>
                  {option.label}
                </span>
                {option.description && (
                  <span className={`block text-xs leading-snug mt-0.5 ${richOptions ? 'text-gray-700 font-medium whitespace-normal' : 'text-gray-400 leading-tight truncate'}`}>
                    {option.description}
                  </span>
                )}
                {(typeof option.meta === 'string' || typeof option.meta === 'number') && (
                  <span className={`block text-xs leading-snug mt-0.5 ${richOptions ? 'text-gray-500 whitespace-normal' : 'text-gray-400 truncate'}`}>
                    {option.meta}
                  </span>
                )}
              </span>
            </button>
          );
        })}

        {loading && (
          <div className="flex items-center justify-center py-3">
            <svg className="animate-spin h-3 w-3 text-indigo-600 mr-1.5" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
            </svg>
            <span className="text-xs text-gray-500">Loading...</span>
          </div>
        )}

        {!loading && pagination.page < pagination.pages && options.length > 0 && (
          <p className="border-t border-gray-100 py-1.5 text-center text-xs text-gray-400">
            Scroll for more results
          </p>
        )}
      </div>

      {onCreateClick && (
        <div className="border-t border-gray-200 bg-gray-50 p-2">
          <button
            type="button"
            onMouseDown={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setIsOpen(false);
              setSearchTerm('');
              onCreateClick();
            }}
            className="w-full rounded-md border border-dashed border-indigo-300 bg-white px-3 py-2 text-left text-xs font-semibold text-indigo-700 hover:bg-indigo-50 hover:border-indigo-400 transition-colors"
          >
            {createLabel}
          </button>
        </div>
      )}
    </div>
  ) : null;

  return (
    <div ref={containerRef} className={`relative w-full ${className}`}>
      {label && (
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {label} {required && <span className="text-red-500">*</span>}
        </label>
      )}

      {variant === 'combobox' ? (
        <div
          className={`w-full rounded-md border shadow-sm flex items-center justify-between transition-all overflow-hidden ${sizeClasses[size] || sizeClasses.sm} ${
            error
              ? 'border-red-300 bg-red-50 ring-1 ring-red-400'
              : 'border-gray-300 bg-white focus-within:border-indigo-500 focus-within:ring-2 focus-within:ring-indigo-500'
          } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
        >
          <input
            ref={triggerInputRef}
            type="text"
            readOnly
            value={closedInputText}
            onFocus={() => openDropdown()}
            placeholder={closedInputText ? '' : placeholder}
            disabled={disabled}
            className={`w-full bg-transparent border-0 ${inputPadding[size] || inputPadding.sm} focus:outline-none focus:ring-0 ${sizeClasses[size] || sizeClasses.sm} ${
              closedInputMuted ? 'text-gray-400' : 'text-gray-900'
            } ${disabled ? 'cursor-not-allowed' : 'cursor-text'}`}
          />
          <div className="flex items-center gap-1 flex-shrink-0 pr-2">
            {allowClear && hasSelection && !disabled && (
              <button type="button" onClick={handleClear} className="text-gray-300 hover:text-gray-500 transition-colors" title="Clear">
                <XMarkIcon className="h-3.5 w-3.5" />
              </button>
            )}
            <button
              type="button"
              onMouseDown={handleChevronMouseDown}
              disabled={disabled}
              className="rounded p-0.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors disabled:cursor-not-allowed"
            >
              <ChevronDownIcon className={`${chevronSizes[size] || chevronSizes.sm} transition-transform ${isOpen ? 'rotate-180' : ''}`} />
            </button>
          </div>
        </div>
      ) : (
        <button
          type="button"
          onClick={openDropdown}
          disabled={disabled}
          className={`w-full rounded-md border bg-white text-left font-normal transition-colors disabled:opacity-50 disabled:cursor-not-allowed disabled:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-indigo-500 ${sizeClasses[size] || sizeClasses.sm} ${
            error
              ? 'border-red-300 bg-red-50'
              : isMinimal
                ? 'border-gray-300 focus:border-indigo-500'
                : 'border-gray-300 shadow-sm focus:border-transparent'
          }`}
        >
          <div className="flex items-center justify-between gap-2">
            <span className={`block truncate ${hasSelection || displayValue || isMinimal ? 'text-gray-900' : 'text-gray-400'}`}>
              {displayValue || placeholder}
            </span>
            <div className="flex items-center gap-1 flex-shrink-0">
              {allowClear && hasSelection && !disabled && (
                <span
                  onClick={handleClear}
                  className="text-gray-300 hover:text-gray-500 transition-colors"
                  title="Clear selection"
                >
                  <XMarkIcon className="h-3.5 w-3.5" />
                </span>
              )}
              <ChevronDownIcon
                className={`${chevronSizes[size] || chevronSizes.sm} text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`}
              />
            </div>
          </div>
        </button>
      )}

      {typeof document !== 'undefined' && dropdownMenu && createPortal(dropdownMenu, document.body)}

      {error && <p className="mt-1 text-xs text-red-600">{error}</p>}
    </div>
  );
};

export default AsyncSearchableSelect;